import { useRef, useCallback, useLayoutEffect } from 'react';
import { Backdrop, Box, IconButton } from '@mui/material';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import CalendarModal from './CalendarModal';
import OccurrenceModal from './OccurrenceModal';
import DeleteTripModal from './DeleteTripModal';
import { useOverlayStore } from '../stores/useOverlayStore';
import { useScreenStore } from '../stores/useScreenStore';
import { useVehicleConflictStore } from '../stores/useVehicleConflictStore';
import { useCalendarModalStore } from '../stores/useCalendarModalStore';

const OverlayRoot = () => {

  const boundsRef = useRef();
  
  const nodeRef = useRef(null);

  const overlay = useOverlayStore(state => state.overlay);

  const canClose = useOverlayStore(state => state.canClose);

  const closeOverlay = useOverlayStore(state => state.closeOverlay);

  const overlayPosition = useOverlayStore(state => state.overlayPosition);

  const setOverlayPosition = useOverlayStore(state => state.setOverlayPosition);

  const preferredScreen = useOverlayStore(state => state.preferredScreen);

  const setPreferredScreen = useOverlayStore(state => state.setPreferredScreen);

  const monitorMode = useScreenStore(state => state.monitorMode);
  
  const screen = useScreenStore(state => state.screen);

  const shouldRenderBackdrop = overlay.open;

  const shouldRenderContent =
    overlay.open &&
    (
      monitorMode === 1 ||
      preferredScreen === screen
    );

  const handleStart = useCallback((_, data) => {
    
    const rect = nodeRef.current.getBoundingClientRect();

    boundsRef.current = {

      left:
        monitorMode === 1
          ? data.x - rect.left
          : -Infinity,

      right:
        monitorMode === 1
          ? window.innerWidth - (rect.right - data.x)
          : Infinity,

      top: data.y - rect.top,

      bottom: window.innerHeight - (rect.bottom - data.y)

    };

  }, [monitorMode]);

  const handleDrag = useCallback(() => {

    if (monitorMode !== 2) {
        return;
    }

    const rect = nodeRef.current?.getBoundingClientRect();

    if (!rect) {
        return;
    }

    const halfWidth = rect.width / 2;

    if (
        screen === 'Home' &&
        rect.right - halfWidth > window.innerWidth
    ) {
        if (preferredScreen !== 'Filtros') {
            setPreferredScreen('Filtros');
        }
        return;
    }

    if (
        screen === 'Filtros' &&
        rect.left + halfWidth < 0
    ) {
        if (preferredScreen !== 'Home') {
            setPreferredScreen('Home');
        }
    }

  }, [
    monitorMode,
    screen,
    preferredScreen,
    setPreferredScreen
  ]);

  const handleStop = useCallback(() => {

    const rect = nodeRef.current?.getBoundingClientRect();

    if (!rect) {
        return;
    }

    setOverlayPosition({
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2
    });

  }, [setOverlayPosition]);

  const closeCalendarOverlay = useCallback(() => {

      useVehicleConflictStore
          .getState()
          .clearAll();

      useCalendarModalStore
          .getState()
          .clear();

      closeOverlay();

  }, [closeOverlay]);

  const handleCloseOverlay = useCallback(() => {

      if (overlay.type === 'calendar') {
          closeCalendarOverlay();
          return;
      }

      closeOverlay();

  }, [
      overlay.type,
      closeCalendarOverlay,
      closeOverlay
  ]);

  useLayoutEffect(() => {
    
    if (!overlay.open || monitorMode !== 1) {
        return;
    }

    const rect = nodeRef.current?.getBoundingClientRect();

    if (!rect) {
        return;
    }

    let centerX = rect.left + rect.width / 2;
    let centerY = rect.top + rect.height / 2;

    if (rect.left < 0) {
        centerX -= rect.left;
    }

    if (rect.right > window.innerWidth) {
        centerX -= rect.right - window.innerWidth;
    }

    if (centerX !== rect.left + rect.width / 2) {

        setOverlayPosition({
            centerX,
            centerY
        });

    }

  }, [
      overlay.open,
      monitorMode,
      setOverlayPosition
  ]);

  if (!shouldRenderBackdrop && !shouldRenderContent) {
    return null;
  }

  return (
    <>
      <Backdrop
        open
        sx={(theme) => ({
          zIndex: theme.zIndex.modal,
          backgroundColor: theme.palette.background.backdrop
        })}
      />
        {shouldRenderContent && (
          <Draggable
            nodeRef={nodeRef}
            handle=".overlay-drag-handle"
            positionOffset={{
                x: '-50%',
                y: '-50%'
            }}
            defaultPosition={{
                x: overlayPosition.centerOffsetX,
                y: overlayPosition.centerOffsetY
            }}
            bounds={boundsRef.current}
            onStart={handleStart}
            onStop={handleStop}
            onDrag={handleDrag}
          >

            <Box
              ref={nodeRef}
              sx={{
                position: 'fixed',
                zIndex: 1300,
                top: '50%',
                left: '50%'
              }}
            >
               <Box
                sx={(theme) => ({
                  ...theme.customStyles.modalPaper,
                  position: 'relative'
                })}
              >
                <Box
                  className="overlay-drag-handle"
                  sx={{
                    height: 20,
                    cursor: 'grab',
                    userSelect: 'none',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',

                    '&:active': {
                      cursor: 'grabbing'
                    },

                    '&::after': {
                      content: '""',
                      width: 40,
                      height: 4,
                      borderRadius: 999,
                      bgcolor: 'text.disabled',
                      opacity: 0.5
                    }
                  }}
                />
                <IconButton
                  disabled={!canClose}
                  onClick={handleCloseOverlay}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1301,
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {overlay.type === 'calendar' && (
                  <CalendarModal
                    line={overlay.payload?.line ?? overlay.payload?.trip}
                    mode={overlay.payload?.mode}
                    offset={overlay.payload?.offset}
                    onClose={handleCloseOverlay}
                  />
                )}

                {overlay.type === 'occurrence' && (
                  <OccurrenceModal
                    trip={overlay.payload?.trip}
                    occurrence={overlay.payload?.occurrence}
                    onClose={handleCloseOverlay}
                  />
                )}

                {overlay.type === 'delete_trip' && (
                  <DeleteTripModal
                    trip={overlay.payload?.trip}
                    onClose={handleCloseOverlay}
                  />
                )}
              </Box>
            </Box>
          </Draggable>
        )}
    </>
  );
};

export default OverlayRoot;